import { EMT_PAGE_NAMES, PAGE_TYPE } from '../../constants';
import { getCurrentPromoImageSrc } from '../../helpers/pps-helpers';
import { buildPagePerStoreBreadcrumbs } from '../../structured-data/build-pps-breadcrumbs';

/**
 * Generates the description for the garden center detail page.
 *
 * @param {Object} params - Parameters for generating the description.
 * @param {string} params.storeName - The name of the store.
 * @returns {string} The generated description for the garden center detail page.
 */
export const generateGardenCenterDesc = ({ storeName }) => {
  if (!storeName) {
    return 'The Home Depot';
  }
  return (
    `Find everything you need for your outdoor projects at the ${storeName} Home Depot’s`
    + ' Garden Center. Our garden store carries top brand garden supplies, '
    + 'all types of plants, garden decor and more.'
  );
};

/**
 * Generates an array of preload image objects for garden center promotional images.
 * Includes the default garden center banner and any active promotion image for the garden center page.
 *
 * @param {Object} storeFinderPromotions - An object containing promotion data with image URLs, typically from emtData.
 * @returns {Array} An array of LinkTag objects for preloading promotional images.
 */
export const getGardenCenterPreloadImages = (storeFinderPromotions) => {
  const gardenCenterBanner = 'https://assets.thdstatic.com/images/v1/garden-center-banner.jpg';
  const images = [{ href: gardenCenterBanner }];

  const promotionImgUrl = getCurrentPromoImageSrc(
    storeFinderPromotions,
    EMT_PAGE_NAMES.GARDEN_CENTER
  );
  if (promotionImgUrl) {
    images.push({ href: promotionImgUrl });
  }

  return images;
};

/**
 * Generates an array of schema objects for use in generating garden center structured data script tags.
 * Each schema object will have both a `content` property (the JSON-LD string) and an `id`.
 * If either property is missing, that schema object will be ignored downstream.
 *
 * If `emtSchema` is provided, it will be used as the only schema in the array.
 * Otherwise, a default breadcrumb schema is generated using the provided store information.
 *
 * @param {Object} params - Parameters for generating schemas.
 * @param {string} params.emtSchema - A string containing the JSON-LD schema content for the garden center.
 * @param {Object} params.storeInfo - The store information object.
 * @returns {Array} An array of schema objects, each with a `content` (string) and `id` (string).
 */
export const getGardenCenterSchemas = ({ emtSchema, storeInfo }) => {
  if (emtSchema) {
    return [{ content: emtSchema, id: 'gardenCenterStructuredData' }];
  }
  return ([{
    content: buildPagePerStoreBreadcrumbs({
      storeInfo,
      departmentName: 'Garden Center',
      departmentID: PAGE_TYPE.GARDEN_CENTER
    }),
    id: 'gardenCenterStructuredBreadcrumb'
  }]);
};